import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Pie } from 'react-chartjs-2';
import { hexToRgba } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { CardTitle, Col, Row } from 'reactstrap';

import { setAlarm } from '../../../../redux/actions/general';
import { CsvExportFromApiButton, HorizontalSeparator, Spinner } from '../../../common';
import { chartjsColors, pieZoomChartOptions } from '../../../../constants/chartjs';
import {
   CenteredMsgShowing,
   ChartWrapper,
   HeightSetContainer,
   SetChartHeight,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../../styledComponents/styledComponents';
import { getFilterQueryParams, getHttpHeader } from '../../../../util/FetchTools';
import {
   hashSelector,
   startTimespanSelector,
   endTimespanSelector,
   titleTimespanSelector,
   cancelTokenSelector,
   tagsSelector,
   streamNamesSelector,
} from '../../../../redux/selectors/selectors';
import useDataFetch from '../../../../hooks/useDataFetch';
import ZoomHint from '../../../zoom/ZoomHint';
import TrafficHint from '../../../common/Hints/TrafficHint';
import { format_time_Number } from '../../../../util/NumberFormatter';
import Modal from '../../../zoom/templates/DefaultTemplate';
import { LIVECAPTIONS_SOURCE_LANGUAGE, LIVECAPTIONS_TARGET_LANGUAGE } from '../../../zoom/ZoomConstants';
import { WIDGET_SETTINGS } from '../../../zoom/widgetSettings/LiveCaptionsView';
import { DefaultGrey } from '../../../../constants/colors';
import { LIVE_CAPTIONS_VIEW } from '../../../../constants/general';
import ErrorDisplay from '../../misuse/ErrorDisplay';

function LanguagePieChart({
   height,
   apiPath,
   csvFilename,
   cardTitle,
   tooltipHtml,
   zoomMetric,
   getLangKey,
   sortByUptime,
}) {
   const [chartData, setChartData] = useState({
      labels: [],
      datasets: [{}],
   });
   const [noData, setNoData] = useState(false);
   const [rawTerms, setRawTerms] = useState([]);
   const [modal, setModal] = useState({
      toggleShow: false,
      selectedTerm: '',
   });

   const chartRef = useRef(null);
   const dispatch = useDispatch();

   const hash = useSelector(hashSelector);
   const start = useSelector(startTimespanSelector);
   const end = useSelector(endTimespanSelector);
   const timespanTitle = useSelector(titleTimespanSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const tags = useSelector(tagsSelector);
   const streamNames = useSelector(streamNamesSelector);

   const header = useMemo(() => getHttpHeader(hash), [hash]);
   const urlFilterQuery = getFilterQueryParams([], tags, [], streamNames, LIVE_CAPTIONS_VIEW);
   const url = `${apiPath}?from=${start.format()}&to=${end.format()}${urlFilterQuery}`;

   const { successful, isLoading, errorMessage, data } = useDataFetch(url, {
      header,
      cancelToken,
   });

   const chartOptions = useMemo(() => {
      const options = pieZoomChartOptions();
      options.onClick = (event, item) => {
         if (item.length === 0) return;
         const { _index } = item[0];
         if (rawTerms.length <= _index) return;

         setModal({
            selectedTerm: rawTerms[_index],
            toggleShow: true,
         });
      };
      return options;
   }, [rawTerms]);

   useEffect(() => {
      if (successful) {
         let languageBreakdown = data.data || [];

         if (languageBreakdown.length === 0) {
            setNoData(true);
            setChartData({ labels: [], datasets: [{}] });
            return;
         }

         let rows = languageBreakdown.map((item) => ({
            lang: getLangKey(item),
            uptime: item.uptime?.seconds ?? 0,
         }));

         if (sortByUptime) rows = rows.sort((a, b) => b.uptime - a.uptime);

         const totalUptime = rows.reduce((sum, { uptime }) => sum + uptime, 0);

         const datasets = [
            {
               label: 'Uptime',
               borderWidth: 1,
               hidden: false,
               backgroundColor: [],
               borderColor: [],
               data: [],
               labels: [],
            },
         ];
         const terms = [];

         rows.forEach(({ lang, uptime: uptimeSeconds }, idx) => {
            const uptimeHours = uptimeSeconds / 3600;
            const percentage = totalUptime > 0 ? (uptimeSeconds / totalUptime) * 100 : 0;

            datasets[0].backgroundColor.push(hexToRgba(chartjsColors[idx % chartjsColors.length], 25));
            datasets[0].borderColor.push(chartjsColors[idx % chartjsColors.length]);
            datasets[0].data.push(uptimeHours);

            const labelName = lang.length > 30 ? `${lang.substring(0, 27)}...` : lang;
            datasets[0].labels.push(
               `${labelName} ( ${percentage < 0.1 ? '<0.1' : percentage.toFixed(1)}% | ${format_time_Number(
                  uptimeSeconds,
               )} )`,
            );

            terms.push(lang);
         });

         setRawTerms(terms);
         setNoData(false);
         setChartData({
            labels: datasets[0].labels,
            datasets,
         });
      }

      if (errorMessage) {
         dispatch(setAlarm('danger', errorMessage));
      }
   }, [data, successful, errorMessage, dispatch, getLangKey, sortByUptime]);

   const errorOrNoData = errorMessage || noData;

   return (
      <WidgetContainer>
         <WidgetHeaderContainer>
            <HeightSetContainer $heightInPx={23}>
               <TwoSideFlexContainer>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <CsvExportFromApiButton
                           url={url}
                           header={header}
                           cancelToken={cancelToken}
                           filename={csvFilename}
                        />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <TrafficHint sizePx={16} customTip={tooltipHtml} />
                     </SimpleFlexItem>
                     <HorizontalSeparator
                        color={DefaultGrey(1, 180)}
                        widthInPx={1}
                        heightInPx={18}
                        marginX_InPx={12}
                        marginTopInPx={0}
                     />
                     <SimpleFlexItem>
                        <ZoomHint />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
               </TwoSideFlexContainer>
            </HeightSetContainer>
         </WidgetHeaderContainer>
         <WidgetBodyContainer>
            <Row>
               <Col>
                  <CardTitle className='mb-0'>{cardTitle}</CardTitle>
                  <div className='small text-muted'>{`${timespanTitle}\u00A0\u00A0\u00A0( UTC time )`}</div>
               </Col>
            </Row>
            <ChartWrapper className='chart-wrapper' marginTopPx={20} height={height}>
               <Spinner loading={isLoading} parentTopMarginPx={60}>
                  {errorOrNoData ? (
                     <CenteredMsgShowing height={height}>
                        {errorMessage ? (
                           <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'}></ErrorDisplay>
                        ) : (
                           'No data available'
                        )}
                     </CenteredMsgShowing>
                  ) : (
                     <SetChartHeight height={height}>
                        <Pie ref={chartRef} options={chartOptions} data={chartData} height={height} />
                     </SetChartHeight>
                  )}
               </Spinner>
            </ChartWrapper>
         </WidgetBodyContainer>
         {modal.toggleShow && (
            <Modal
               term={modal.selectedTerm}
               timespan={{ start, end }}
               metric={zoomMetric}
               settings={WIDGET_SETTINGS}
               closeModal={() => setModal({ ...modal, toggleShow: false })}
            />
         )}
      </WidgetContainer>
   );
}

const isValidLanguageCode = (code) => /^[a-zA-Z]{2,8}(-[a-zA-Z0-9]{1,8})*$/.test(code);

const LANGUAGE_CONFIG = {
   source: {
      apiPath: '/v2/captions/usage/language/source',
      csvFilename: 'LiveCaptions_SourceLanguage',
      cardTitle: 'Uptime per Source Language',
      tooltipHtml:
         '<div>This chart shows the distribution of transcription</div>' +
         '<div>session uptime by source language — the language</div>' +
         '<div>being transcribed. Each slice represents the share</div>' +
         '<div>of total uptime for that source language.</div>',
      zoomMetric: LIVECAPTIONS_SOURCE_LANGUAGE,
      getLangKey: (item) => (isValidLanguageCode(item.sourceLanguage || '') ? item.sourceLanguage : 'Unknown'),
      sortByUptime: true,
   },
   target: {
      apiPath: '/v2/captions/usage/language/target',
      csvFilename: 'LiveCaptions_TargetLanguage',
      cardTitle: 'Uptime per Target Language',
      tooltipHtml:
         '<div>This chart shows the distribution of transcription</div>' +
         '<div>session uptime by target language — the language</div>' +
         '<div>being translated into (e.g. "en, de, fr").</div>',
      zoomMetric: LIVECAPTIONS_TARGET_LANGUAGE,
      getLangKey: (item) => (isValidLanguageCode(item.targetLanguage || '') ? item.targetLanguage : 'Unknown'),
      sortByUptime: true,
   },
};

export function SourceLanguage({ height }) {
   return <LanguagePieChart height={height} {...LANGUAGE_CONFIG.source} />;
}

export function TargetLanguage({ height }) {
   return <LanguagePieChart height={height} {...LANGUAGE_CONFIG.target} />;
}
